import find from 'lodash/find'

import { fetchJson } from '../../GlobalStateContainer'
import { isValidGuid } from '../../../util/guidUtility'
import {
  NOTIFY_ERROR,
  USE_BILLING_FOR_SHIPPING_TOGGLE,
  ADD_SHIPPING_ADDRESS,               
  ADD_BILLING_ADDRESS,                
  REMOVE_SHIPPING_ADDRESS,            
  REMOVE_BILLING_ADDRESS,             
  UPDATE_ADDRESS,                     
  SELECT_BILLING_ADDRESS,             
  SELECT_SHIPPING_ADDRESS,             
  SELECTING_BILLING_ADDRESS_ASYNC,             
  SELECTING_SHIPPING_ADDRESS_ASYNC,            
  SELECTING_SHIPPINGANDBILLING_ADDRESS_ASYNC,            
  SAVE_ADDRESS_ASYNC,                 
  DELETE_ADDRESS_ASYNC,               
  SELECT_SHIPPING_AND_BILLING_ADDRESS
} from '../../GlobalStateContainer'

import {
  notifyError,
  logError
} from '../UserMessaging'
/*
 *  SYNC ACTION CREATORS
 */

let nextAddressId = -1;

let setAddressIdIfMissing = (newAddress) => {
  if (newAddress.Id === "")
    return Object.assign({}, { Id: (nextAddressId--).toString() }, newAddress);
  else if (newAddress.Id < 0) {
    return notifyError("An error occurred trying to add the address. Please let us know!", "address already has an Id field that can be cast to a negative number");
  }
}

export function addBillingAddress(newAddress) {
  var addressWithIdOrError = setAddressIdIfMissing(newAddress);
  if (addressWithIdOrError && addressWithIdOrError.type && addressWithIdOrError.type === NOTIFY_ERROR)
    return addressWithIdOrError;
  return { type: ADD_BILLING_ADDRESS, address: addressWithIdOrError }
}

export function addShippingAddress(newAddress) {
  var addressWithIdOrError = setAddressIdIfMissing(newAddress);
  if (addressWithIdOrError && addressWithIdOrError.type && addressWithIdOrError.type === NOTIFY_ERROR)
    return addressWithIdOrError;
  return { type: ADD_SHIPPING_ADDRESS, address: addressWithIdOrError }
}

export function removeShippingAddress(addressId) {
  return { type: REMOVE_SHIPPING_ADDRESS, addressId }
}

export function removeBillingAddress(addressId) {
  return { type: REMOVE_BILLING_ADDRESS, addressId }
}

export function selectBillingAddress(addressId) {
  return { type: SELECT_BILLING_ADDRESS, addressId }
}

export function selectShippingAddress(addressId) {
  return { type: SELECT_SHIPPING_ADDRESS, addressId }
}

export function updateAddress(addressId, address) {
  return { type: UPDATE_ADDRESS, addressId, address }
}

export function selectShippingAndBillingAddress(addressId) {
  return { type: SELECT_SHIPPING_AND_BILLING_ADDRESS, addressId }
}

export function useBillingForShippingToggle() {
  return { type: USE_BILLING_FOR_SHIPPING_TOGGLE }
}


/*
 *  ASYNC ACTION CREATORS
 */

const onPostAddressSucceeded = (responseText, postedAddress, getState) => {
  if (isValidGuid(responseText)) {
    const updatedAddress = Object.assign({}, postedAddress, { Id: responseText });
    const updateAddressAction = updateAddress(postedAddress.Id, updatedAddress);
    if (postedAddress.Id === '') {
      const state = getState();
      const dataPayload = {addressId: responseText};
      const selectAction = state.ui.useShippingForBilling
        ? selectShippingAndBillingAddressAsync(dataPayload)
        : (postedAddress.AddressType === state.constants.ShippingAddressType 
            ? selectShippingAddressAsync(dataPayload)
            : selectBillingAddressAsync(dataPayload));
      return [updateAddressAction, selectAction];
    }
    return updateAddressAction;
  } else {
    return notifyError('An error occurred saving an address', `Expected to receive a guid when posting an address but got: '${responseText}'`)
  };
};

const saveAddressFetchSpec = {
  endpoint : state => state.constants.navigation.addAddressEndpoint,
  fetchInit: { method: 'POST', credentials: 'same-origin' },
  notify   : address => ( { type: SAVE_ADDRESS_ASYNC, address } ),
  extract  : response => response.text() ,
  onSuccess: onPostAddressSucceeded,
  onError  : diagMsg => notifyError("An error occurred saving an address", diagMsg)
};

export const saveAddress = fetchJson(saveAddressFetchSpec);

const onPostDeleteSucceeded = (responseText, { addressId }, getState) => {
  if (responseText.toUpperCase() !== "OK")
    return notifyError('An error occurred deleting an address',
      `Expected to receive 'OK' when deleting an address but got '${responseText}'`);
  else {
    const state = getState();
    const postedAddress = find(state.entities.Addresses, address => address.Id === addressId);
    if (!postedAddress)
      return logError(`No address to delete with id ${addressId}`);
    return (postedAddress.AddressType === state.constants.BillingAddressType)
      ? removeBillingAddress(postedAddress.Id)
      : removeShippingAddress(postedAddress.Id);
  }
};

const deleteAddressFetchSpec = {
  endpoint : (state) => state.constants.navigation.deleteAddressEndpoint,
  fetchInit: { method: 'POST', credentials: 'same-origin' },
  notify   : ({ addressId }) => ( { type: DELETE_ADDRESS_ASYNC, addressId } ),
  extract  : (response) => response.text() ,
  onSuccess: onPostDeleteSucceeded,
  onError  : (diagMsg) => notifyError("An error occurred deleting an address", diagMsg)
}

export const deleteAddress = fetchJson(deleteAddressFetchSpec)


const onSelectBillingAddressSucceeded = (responseText, { addressId }, getState) => {
  if (responseText.toUpperCase() !== "OK")
    return notifyError('An error occurred while selecting your billing address',
      `Expected to receive 'OK' when selecting a billing address but got '${responseText}'`);
  else {
    const state = getState();
    const postedAddress = find(state.entities.Addresses, address => address.Id === addressId);
    if (!postedAddress)
      return logError(`No address to select with id ${addressId}`);
    return selectBillingAddress(postedAddress.Id);
  }
};

const onSelectShippingAddressSucceeded = (responseText, { addressId }, getState) => {
  if (responseText.toUpperCase() !== "OK")
    return notifyError('An error occurred selecting your shipping address',
      `Expected to receive 'OK' when selecting a shipping address but got '${responseText}'`);
  else {
    const state = getState();
    const postedAddress = find(state.entities.Addresses, address => address.Id === addressId);
    if (!postedAddress)
      return logError(`No address to select with id ${addressId}`);
    return selectShippingAddress(postedAddress.Id);
  }
};
const onSelectShippingAndBillingAddressSucceeded = (responseText, { addressId }, getState) => {
  if (responseText.toUpperCase() !== "OK")
    return notifyError('An error occurred selecting your shipping address',
      `Expected to receive 'OK' when selecting billing and shipping address but got '${responseText}'`);
  else {
    const state = getState();
    const postedAddress = find(state.entities.Addresses, address => address.Id === addressId);
    if (!postedAddress)
      return logError(`No address to select with id ${addressId}`);
    return selectShippingAndBillingAddress(postedAddress.Id);
  }
};

const selectBillingAddressFetchSpec = {
  endpoint : (state) => state.constants.navigation.updateSelectedAddressEndpoint,
  fetchInit: {method: 'POST', credentials: 'same-origin'},
  notify   : ({addressId}) =>  ({ type: SELECTING_BILLING_ADDRESS_ASYNC, addressId }),
  extract  : (response) => response.text(),
  onSuccess: onSelectBillingAddressSucceeded,
  onError  : (diagMsg) => notifyError("An error occurred updating the selected billing address", diagMsg)
}

export const selectBillingAddressAsync = fetchJson(selectBillingAddressFetchSpec)


const selectShippingAddressFetchSpec = {
  endpoint : (state) => state.constants.navigation.updateSelectedAddressEndpoint,
  fetchInit: {method: 'POST', credentials: 'same-origin'},
  notify   : ({ addressId }) => ({ type: SELECTING_SHIPPING_ADDRESS_ASYNC, addressId }),
  extract  : (response) => response.text(),
  onSuccess: onSelectShippingAddressSucceeded,
  onError  : (diagMsg) => notifyError("An error occurred updating the selected shipping address", diagMsg)
}

export const selectShippingAddressAsync = fetchJson(selectShippingAddressFetchSpec)


const selectShippingAndBillingAddressFetchSpec = {
  endpoint : (state) => state.constants.navigation.updateSelectedAddressEndpoint,
  fetchInit: {method: 'POST', credentials: 'same-origin'},
  notify   : ({ addressId }) => ({ type: SELECTING_SHIPPINGANDBILLING_ADDRESS_ASYNC, addressId }),
  extract  : (response) => response.text(),
  onSuccess: onSelectShippingAndBillingAddressSucceeded,
  onError  : (diagMsg) => notifyError("An error occurred updating the selected ShippingAndBilling address", diagMsg)
}

export const selectShippingAndBillingAddressAsync = fetchJson(selectShippingAndBillingAddressFetchSpec)


// WEBPACK FOOTER //
// ./src/Components/checkout/Features/Address/addressActionCreators.js